import { Badge, Button, Group, Image, Stack, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { CrossCameraLookalike } from '@/types/crossCamera'
import { getFormattedDateWithTime } from '@/utils/date'

const CROP_HEIGHT = 120
const POSSIBLE_BELOW_THRESHOLD = 0.1

enum Closeness {
  Likely = 'likely',
  Possible = 'possible',
  Unlikely = 'unlikely'
}

const CLOSENESS_CONFIG = {
  [Closeness.Likely]: {
    color: 'teal',
    translationId: 'crossCamera.lookalikes.closeness.likely'
  },
  [Closeness.Possible]: {
    color: 'yellow',
    translationId: 'crossCamera.lookalikes.closeness.possible'
  },
  [Closeness.Unlikely]: {
    color: 'gray',
    translationId: 'crossCamera.lookalikes.closeness.unlikely'
  }
}

const closenessOf = (score: number, threshold: number) => {
  if (score >= threshold) return Closeness.Likely
  if (score >= threshold - POSSIBLE_BELOW_THRESHOLD) return Closeness.Possible
  return Closeness.Unlikely
}

type LookalikeListProps = {
  sourceIdentityId: string | null
  samePersonByTrackletId: Record<string, boolean>
  lookalikes: CrossCameraLookalike[]
  threshold: number
  canLink?: boolean
  pendingTrackletId?: string
  onOpenEventClick: (eventId: string) => void
  onSamePersonClick: (trackletId: string) => void
  onNotSamePersonClick: (trackletId: string) => void
}

export const LookalikeList = ({
  sourceIdentityId,
  samePersonByTrackletId,
  lookalikes,
  threshold,
  canLink,
  pendingTrackletId,
  onOpenEventClick,
  onSamePersonClick,
  onNotSamePersonClick
}: LookalikeListProps) => {
  const intl = useIntl()

  if (lookalikes.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        <FormattedMessage id="crossCamera.lookalikes.empty" />
      </Text>
    )
  }

  return (
    <Stack gap="md">
      {lookalikes.map(
        ({
          tracklet_id,
          camera_name,
          camera_id,
          first_seen,
          score,
          crop_path,
          event_id,
          identity_id
        }) => {
          const closeness = CLOSENESS_CONFIG[closenessOf(score, threshold)]
          const isSamePerson =
            samePersonByTrackletId[tracklet_id] ??
            (!!sourceIdentityId && identity_id === sourceIdentityId)
          const isPending = pendingTrackletId === tracklet_id
          const isOtherPending = !!pendingTrackletId && !isPending

          return (
            <Group key={tracklet_id} wrap="nowrap" align="flex-start" gap="md">
              {crop_path && (
                <Image
                  h={CROP_HEIGHT}
                  w="auto"
                  fit="contain"
                  src={crop_path}
                  alt={intl.formatMessage({ id: 'crossCamera.people.crop' })}
                />
              )}

              <Stack gap={4}>
                <Group gap="xs">
                  <Badge color={closeness.color} variant="light">
                    <FormattedMessage id={closeness.translationId} />
                  </Badge>

                  <Text size="sm" ff="monospace">
                    {score.toFixed(2)}
                  </Text>

                  {isSamePerson && (
                    <Badge color="blue" variant="outline">
                      <FormattedMessage id="crossCamera.lookalikes.alreadyLinked" />
                    </Badge>
                  )}
                </Group>

                <Text size="sm" fw={600}>
                  <span>{camera_name || camera_id}</span>
                </Text>

                <Text size="xs" c="dimmed">
                  <span>{getFormattedDateWithTime(first_seen * 1000)}</span>
                </Text>

                <Group gap="xs">
                  {event_id && (
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      onClick={() => onOpenEventClick(event_id)}
                    >
                      <FormattedMessage id="crossCamera.lookalikes.openEvent" />
                    </Button>
                  )}

                  {canLink && isSamePerson && (
                    <Button
                      variant="subtle"
                      color="red"
                      size="compact-xs"
                      loading={isPending}
                      disabled={isOtherPending}
                      onClick={() => onNotSamePersonClick(tracklet_id)}
                    >
                      <FormattedMessage id="crossCamera.lookalikes.notSamePerson" />
                    </Button>
                  )}

                  {canLink && !isSamePerson && (
                    <Button
                      variant="light"
                      size="compact-xs"
                      loading={isPending}
                      disabled={isOtherPending}
                      onClick={() => onSamePersonClick(tracklet_id)}
                    >
                      <FormattedMessage id="crossCamera.lookalikes.samePerson" />
                    </Button>
                  )}
                </Group>
              </Stack>
            </Group>
          )
        }
      )}
    </Stack>
  )
}
